import React, { useEffect, useState } from "react";
import { ClipboardPaste, X, ExternalLink, CalendarIcon, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

import { CANADIAN_PROVINCES, OCP_LOOKUP_URL } from "@/lib/pp/constants";
import { parseOcpText } from "@/lib/pp/ocpParser";
import { api } from "@/lib/pp/api";
import { toLocalYMD, fromYMD } from "@/lib/pp/dates";
import { formatPhone } from "@/lib/formatPhone";

const emptyForm = {
  name: "",
  status: "Active",
  site_type: "Pharmacy",
  address: "",
  city: "",
  province: "ON",
  postal_code: "",
  phone: "",
  fax: "",
  email: "",
  ocp_license: "",
  contact_first_name: "",
  contact_last_name: "",
  membership_date: null,
  notes: "",
};

export default function PharmacyFormDialog({
  open,
  onOpenChange,
  editing,
  statuses,
  onAddStatus,
  onSaved,
  mode = "partners",   // "partners" (default) or "bd"
}) {
  const [form, setForm] = useState(emptyForm);
  const [pasteText, setPasteText] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [statusList, setStatusList] = useState(statuses);
  const [statusMgrOpen, setStatusMgrOpen] = useState(false);
  const [statusRename, setStatusRename] = useState(null);
  const [siteTypes, setSiteTypes] = useState(["Pharmacy", "Shelter", "Drop-in", "Outreach"]);
  const [newType, setNewType] = useState("");
  const [typeMgrOpen, setTypeMgrOpen] = useState(false);
  const [typeRename, setTypeRename] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [membershipDateOpen, setMembershipDateOpen] = useState(false);

  const loadSiteTypes = async () => {
    try {
      const { data } = await api.get("/settings/site-types");
      if (data?.values?.length) setSiteTypes(data.values);
    } catch { /* keep defaults */ }
  };

  const loadStatuses = async () => {
    try {
      const { data } = await api.get("/settings/statuses");
      if (data?.values?.length) setStatusList(data.values);
    } catch { /* keep prop list */ }
  };
  const BUILTINS = ["Active", "Inactive"];  // seed values only — all statuses are editable/deletable now

  useEffect(() => {
    if (open) {
      const base = editing ? { ...emptyForm, ...editing } : emptyForm;
      setForm({
        ...base,
        site_type: editing?.site_type || "Pharmacy",
        phone: formatPhone(base.phone),
        fax: formatPhone(base.fax),
      });
      setPasteText("");
      setNewStatus("");
      setStatusMgrOpen(false);
      setStatusRename(null);
      setNewType("");
      setTypeMgrOpen(false);
      setTypeRename(null);
      setErrors({});
      loadSiteTypes();
      loadStatuses();
    }
  }, [open, editing]);

  // Only a true "Pharmacy" type needs the OCP licence + lookup workflow;
  // every other site type (shelter, drop-in, outreach, …) skips it.
  const isPharmacy = (form.site_type || "Pharmacy").trim().toLowerCase() === "pharmacy";

  // BD-mode leads may not have signed on yet, so Start date is optional.
  // Once a Start date is entered, the record also lights up in Partners
  // as an active partner (see auto-Active logic in `save`).
  // Required: identity + address + phone + start date. Email and contact
  // person are optional; OCP # is required only for Pharmacy-type sites.
  const REQUIRED_BASE = [
    "name",
    "province",
    "address",
    "city",
    "postal_code",
    "phone",
    "membership_date",
  ];
  const REQUIRED = [
    ...(mode === "bd" ? REQUIRED_BASE.filter((k) => k !== "membership_date") : REQUIRED_BASE),
    ...(isPharmacy ? ["ocp_license"] : []),
  ];

  const clearErr = (k) =>
    setErrors((e) => (e[k] ? { ...e, [k]: false } : e));

  const errCls = (k) =>
    errors[k]
      ? "border-[#B84A4A] ring-1 ring-[#B84A4A]/40 focus-visible:ring-[#B84A4A]"
      : "";

  const update = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    clearErr(k);
  };
  const upd = (k) => (e) => update(k)(e.target?.value ?? e);

  const applyPaste = (t) => {
    const parsed = parseOcpText(t);
    if (parsed.phone) parsed.phone = formatPhone(parsed.phone);
    if (parsed.fax) parsed.fax = formatPhone(parsed.fax);
    setPasteText(t);
    setForm((f) => ({ ...f, ...parsed }));
    // clear errors on any auto-filled fields
    if (Object.keys(parsed).length) {
      setErrors((e) => {
        const ne = { ...e };
        Object.keys(parsed).forEach((k) => (ne[k] = false));
        return ne;
      });
      toast.success(`Autofilled ${Object.keys(parsed).length} field(s)`);
    }
  };

  const doPaste = async () => {
    try {
      const t = await navigator.clipboard.readText();
      if (t) applyPaste(t);
      else toast.error("Clipboard is empty");
    } catch {
      toast.error("Couldn't read clipboard", {
        description: "Tap the textarea and use Paste manually.",
      });
    }
  };

  const addStatus = async () => {
    const v = newStatus.trim();
    if (!v) return;
    try {
      await api.post("/settings/statuses", { value: v });
      await loadStatuses();
      update("status")(v);
      setNewStatus("");
      toast.success(`"${v}" added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add status");
    }
  };

  const renameStatus = async () => {
    const v = newStatus.trim();
    if (!v || !statusRename) return;
    try {
      await api.patch(`/settings/statuses/${encodeURIComponent(statusRename)}`, { value: v });
      await loadStatuses();
      if (form.status === statusRename) update("status")(v);
      setNewStatus("");
      setStatusRename(null);
      toast.success(`Renamed to "${v}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename status");
    }
  };

  const deleteStatus = async (v) => {
    try {
      await api.delete(`/settings/statuses/${encodeURIComponent(v)}`);
      await loadStatuses();
      if (form.status === v) update("status")("Active");
      toast.success(`"${v}" removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete status");
    }
  };

  const addSiteType = async () => {
    const v = newType.trim();
    if (!v) return;
    try {
      await api.post("/settings/site-types", { value: v });
      await loadSiteTypes();
      update("site_type")(v);
      setNewType("");
      toast.success(`"${v}" added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add type");
    }
  };

  const renameSiteType = async () => {
    const v = newType.trim();
    if (!v || !typeRename) return;
    try {
      await api.patch(`/settings/site-types/${encodeURIComponent(typeRename)}`, { value: v });
      await loadSiteTypes();
      if (form.site_type === typeRename) update("site_type")(v);
      setNewType("");
      setTypeRename(null);
      toast.success(`Renamed to "${v}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename type");
    }
  };

  const deleteSiteType = async (v) => {
    if (!v) return;
    try {
      await api.delete(`/settings/site-types/${encodeURIComponent(v)}`);
      const { data } = await api.get("/settings/site-types");
      const remaining = data?.values || [];
      setSiteTypes(remaining);
      if (form.site_type === v) update("site_type")(remaining[0] || "");
      toast.success(`"${v}" removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete type");
    }
  };

  const save = async () => {
    // required-field validation
    const nextErrors = {};
    for (const k of REQUIRED) {
      const v = form[k];
      if (v == null || String(v).trim() === "") nextErrors[k] = true;
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      toast.error("Please complete all required fields (highlighted in red).");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        membership_date: form.membership_date
          ? typeof form.membership_date === "string"
            ? form.membership_date
            : new Date(form.membership_date).toISOString().slice(0, 10)
          : null,
      };
      // In BD mode, entering a Start date promotes the pharmacy to an
      // active partner so it shows up under Partners as Active.
      if (mode === "bd" && payload.membership_date) {
        payload.status = "Active";
      }
      if (editing) {
        await api.patch(`/pharmacies/${editing.id}`, payload);
        toast.success("Pharmacy updated");
      } else {
        await api.post(`/pharmacies`, payload);
        toast.success("Pharmacy added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      // Surface the real backend reason so we can fix the actual issue,
      // not just show "Save failed" and guess.
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        (typeof e?.response?.data === "string" ? e.response.data : null) ||
        e?.message ||
        "Unknown error";
      const status = e?.response?.status;
      const readable = Array.isArray(detail)
        ? detail
            .map((d) => `${(d.loc || []).slice(-1).join(".")}: ${d.msg}`)
            .join(" · ")
        : String(detail);
      console.error("[pp-save-pharmacy] failed", { status, detail, error: e });
      toast.error(`Save failed${status ? ` (${status})` : ""}: ${readable}`, {
        duration: 10000,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto overflow-x-hidden"
        style={{ width: "calc(100vw - 24px)" }}
        data-testid="pp-pharmacy-form"
      >
        <DialogHeader className="text-left sm:text-left">
          <DialogTitle
            style={{ fontFamily: "'Work Sans', system-ui, sans-serif" }}
            className="text-[#1C2B26] text-left"
          >
            {editing ? "Edit referral site or pharmacy" : "Add referral site or pharmacy"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 min-w-0">
          {/* Site type — Pharmacy keeps the OCP workflow; anything else is a
              referral site (shelter, drop-in, outreach, …) with no licence #.
              Types are managed from a tidy expandable list, not a crowded row. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[#1C2B26]">Type</Label>
              <Button type="button" variant="outline" size="sm" className="h-7 px-2.5 text-xs"
                onClick={() => { setTypeMgrOpen((o) => !o); setTypeRename(null); setNewType(""); }}
                data-testid="pp-site-type-manage-toggle">
                {typeMgrOpen ? "Done" : "Manage"}
              </Button>
            </div>
            <Select value={form.site_type} onValueChange={(v) => update("site_type")(v)}>
              <SelectTrigger
                data-testid="pp-site-type-select"
                className="w-full min-w-0"
              >
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {siteTypes.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`pp-site-type-${t}`}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {typeMgrOpen && (
              <div className="rounded-md border border-[#E5E2DC] p-2.5 space-y-1.5" data-testid="pp-site-type-manager">
                <div className="flex gap-1.5">
                  <Input
                    value={newType}
                    onChange={(e) => setNewType(e.target.value)}
                    placeholder="+ New type"
                    className="flex-1 min-w-0 text-xs h-8"
                    data-testid="pp-new-site-type-input"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addSiteType} disabled={!newType.trim()} data-testid="pp-new-site-type-add" className="shrink-0 px-2.5 text-xs h-8">
                    Add
                  </Button>
                </div>
                {siteTypes.map((t) => (
                  <div key={t} className="flex items-center gap-1.5 py-1 border-t border-[#F0EDE8] first:border-t-0" data-testid={`pp-site-type-row-${t}`}>
                    {typeRename === t ? (
                      <>
                        <Input value={newType} onChange={(e) => setNewType(e.target.value)} className="flex-1 min-w-0 text-xs h-8" data-testid="pp-site-type-rename-input" />
                        <Button type="button" variant="outline" size="sm" onClick={renameSiteType} disabled={!newType.trim()} data-testid="pp-site-type-rename-save" className="shrink-0 px-2 text-xs h-8">Save</Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => { setTypeRename(null); setNewType(""); }} className="shrink-0 h-8 w-8 p-0"><X className="h-3.5 w-3.5" /></Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 min-w-0 text-xs font-medium text-[#1C2B26] truncate">{t}</span>
                        <Button type="button" variant="outline" size="sm" onClick={() => { setTypeRename(t); setNewType(t); }} title="Rename" data-testid={`pp-site-type-rename-${t}`} className="shrink-0 h-8 w-8 p-0">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => deleteSiteType(t)} title="Delete" className="text-[#B84A4A] shrink-0 h-8 w-8 p-0" data-testid={`pp-site-type-delete-${t}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* OCP paste — reads from device clipboard, no visible text field.
              Pharmacies only; referral sites have no OCP register entry. */}
          {isPharmacy && (
          <div className="rounded-md border border-dashed border-[#3B7A57] bg-[#EEF6EF] px-4 py-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-[#2D5A4C] min-w-0">
                <ClipboardPaste className="h-4 w-4 shrink-0" />
                <span className="text-sm font-semibold whitespace-nowrap">
                  Paste from OCP Register
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <a
                  href={OCP_LOOKUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold text-[#2D5A4C] hover:text-[#1F4237] underline underline-offset-2 inline-flex items-center gap-1 whitespace-nowrap"
                  data-testid="pp-ocp-tcpr-link"
                >
                  OCP Lookup
                  <ExternalLink className="h-3 w-3" />
                </a>
                <button
                  type="button"
                  onClick={doPaste}
                  data-testid="pp-ocp-paste-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#2D5A4C] hover:bg-[#1F4237] text-white text-xs font-semibold px-3 py-1.5 transition-colors whitespace-nowrap"
                >
                  <ClipboardPaste className="h-3.5 w-3.5" />
                  Auto-fill from clipboard
                </button>
              </div>
            </div>
          </div>
          )}

          {/* Status — same clean pattern as Type: full-width select, managed
              from an expandable list */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[#1C2B26]">Status</Label>
              <Button type="button" variant="outline" size="sm" className="h-7 px-2.5 text-xs"
                onClick={() => { setStatusMgrOpen((o) => !o); setStatusRename(null); setNewStatus(""); }}
                data-testid="pp-status-manage-toggle">
                {statusMgrOpen ? "Done" : "Manage"}
              </Button>
            </div>
            <Select value={form.status} onValueChange={update("status")}>
              <SelectTrigger
                data-testid="pp-status-select"
                className="w-full min-w-0"
              >
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statusList.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`pp-status-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {statusMgrOpen && (
              <div className="rounded-md border border-[#E5E2DC] p-2.5 space-y-1.5" data-testid="pp-status-manager">
                <div className="flex gap-1.5">
                  <Input
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    placeholder="+ New status"
                    className="flex-1 min-w-0 text-xs h-8"
                    data-testid="pp-new-status-input"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addStatus} disabled={!newStatus.trim()} data-testid="pp-new-status-add" className="shrink-0 px-2.5 text-xs h-8">
                    Add
                  </Button>
                </div>
                {statusList.map((s) => (
                  <div key={s} className="flex items-center gap-1.5 py-1 border-t border-[#F0EDE8] first:border-t-0" data-testid={`pp-status-row-${s}`}>
                    {statusRename === s ? (
                      <>
                        <Input value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="flex-1 min-w-0 text-xs h-8" data-testid="pp-status-rename-input" />
                        <Button type="button" variant="outline" size="sm" onClick={renameStatus} disabled={!newStatus.trim()} data-testid="pp-status-rename-save" className="shrink-0 px-2 text-xs h-8">Save</Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => { setStatusRename(null); setNewStatus(""); }} className="shrink-0 h-8 w-8 p-0"><X className="h-3.5 w-3.5" /></Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 min-w-0 text-xs font-medium text-[#1C2B26] truncate">{s}</span>
                        <Button type="button" variant="outline" size="sm" onClick={() => { setStatusRename(s); setNewStatus(s); }} title="Rename" data-testid={`pp-status-rename-${s}`} className="shrink-0 h-8 w-8 p-0">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => deleteStatus(s)} title="Delete" className="text-[#B84A4A] shrink-0 h-8 w-8 p-0" data-testid={`pp-status-delete-${s}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Start date + OCP # on one row (OCP # only exists for pharmacies) */}
          <div className={`grid gap-3 ${isPharmacy ? "grid-cols-2" : "grid-cols-1"}`}>
            <div className="space-y-2 min-w-0">
              <Label className="text-[#1C2B26]">Start date{mode === "bd" ? "" : " *"}</Label>
              <Popover open={membershipDateOpen} onOpenChange={setMembershipDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="pp-membership-date"
                    className={`h-9 w-full inline-flex items-center gap-2 rounded-md border ${
                      errors.membership_date
                        ? "border-[#B84A4A] ring-1 ring-[#B84A4A]/40"
                        : "border-[#E5E2DC]"
                    } bg-white px-3 text-sm text-[#1C2B26] min-w-0 overflow-hidden`}
                  >
                    <CalendarIcon className="h-3.5 w-3.5 text-[#4A5D55] shrink-0" />
                    <span className="truncate">
                      {form.membership_date
                        ? format(fromYMD(form.membership_date), "MMM d, yyyy")
                        : "Pick date"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={fromYMD(form.membership_date)}
                    onSelect={(d) => {
                      setForm((f) => ({ ...f, membership_date: toLocalYMD(d) }));
                      setMembershipDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            {isPharmacy && (
            <div className="space-y-2 min-w-0">
              <Label>OCP # *</Label>
              <Input
                value={form.ocp_license}
                onChange={upd("ocp_license")}
                data-testid="pp-form-ocp-license"
                className={`w-full min-w-0 ${errCls("ocp_license")}`}
              />
            </div>
            )}
          </div>

          {/* Fields */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={upd("name")} data-testid="pp-form-name" className={errCls("name")} />
            </div>
            <div className="space-y-2">
              <Label>Province *</Label>
              <Select value={form.province} onValueChange={update("province")}>
                <SelectTrigger data-testid="pp-form-province" className={errCls("province")}>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {CANADIAN_PROVINCES.map((p) => (
                    <SelectItem key={p.code} value={p.code}>
                      {p.code} — {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Address *</Label>
              <Input value={form.address} onChange={upd("address")} data-testid="pp-form-address" className={errCls("address")} />
            </div>
            <div className="space-y-2">
              <Label>City *</Label>
              <Input value={form.city} onChange={upd("city")} data-testid="pp-form-city" className={errCls("city")} />
            </div>
            <div className="space-y-2">
              <Label>Postal code *</Label>
              <Input value={form.postal_code} onChange={upd("postal_code")} data-testid="pp-form-postal" className={errCls("postal_code")} />
            </div>
            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input value={form.phone} onChange={(e) => update("phone")(formatPhone(e.target.value))} data-testid="pp-form-phone" className={errCls("phone")} />
            </div>
            <div className="space-y-2">
              <Label>Fax</Label>
              <Input value={form.fax} onChange={(e) => update("fax")(formatPhone(e.target.value))} data-testid="pp-form-fax" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={upd("email")} data-testid="pp-form-email" />
            </div>
            <div className="space-y-2">
              <Label>First name</Label>
              <Input
                value={form.contact_first_name}
                onChange={upd("contact_first_name")}
                data-testid="pp-form-contact-first"
              />
            </div>
            <div className="space-y-2">
              <Label>Last name</Label>
              <Input
                value={form.contact_last_name}
                onChange={upd("contact_last_name")}
                data-testid="pp-form-contact-last"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes / Preferences</Label>
            <Textarea rows={3} value={form.notes} onChange={upd("notes")} data-testid="pp-form-notes" />
          </div>
        </div>

        <div className="flex flex-row items-center justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="pp-form-cancel"
            className="h-9 px-4 text-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            className="h-9 px-4 text-sm bg-[#2D5A4C] hover:bg-[#1F4237] text-white"
            data-testid="pp-form-save"
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
